import { eq, type PowerSyncDrizzleDB, project } from "@time-tracker/powersync";
import { nowISO } from "../utils/date-helpers";
import {
  patchRowVerified,
  type VerifiedWriteOptions,
  waitForRowPresent,
} from "../powersync/verified-write";

/** Billing definition of a project version needed to restore it (bucket/API). */
export interface ProjectVersionSnapshot {
  name: string | null;
  code: string | null;
  color: string | null;
  status: string | null;
  hourly_rate: number | null;
  /** sqlite stores booleans as 0/1 (integer); accept both for API/bucket. */
  billable: number | boolean | null;
  currency: string | null;
}

function toIntBool(v: number | boolean | null | undefined): number | null {
  if (v === null || v === undefined) {
    return null;
  }
  return v ? 1 : 0;
}

/**
 * Restore a version from the history (PROJECT VERSIONING): a NORMAL forward
 * write in ONE transaction that rewrites the project's billing definition to
 * match the snapshot. Never rewrites history: the server write-path materializes
 * the restored definition as a NEW version (current), and existing time-entry
 * SEALS do NOT change. Does NOT touch deleted_at. A FLAT snapshot (no child
 * items), so it is a single UPDATE.
 *
 * Must run in `db.transaction` so PowerSync uploads the gesture as ONE batch
 * (a single new version).
 *
 * Watch out for the PowerSync ghost window (see `verified-write.ts`): inside
 * a transaction the `UPDATE` CANNOT be retried with backoff (sleeping with
 * the write lock held), so we wait for the row to be present
 * in the view BEFORE opening the transaction, and inside perform a single attempt.
 */
export async function restoreProjectVersionMutation(
  db: PowerSyncDrizzleDB,
  projectId: string,
  version: ProjectVersionSnapshot,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await waitForRowPresent(db, project, project.id, projectId, options);
  await db.transaction(async (trx) => {
    await trx
      .update(project)
      .set({
        name: version.name ?? "",
        code: version.code ?? "",
        color: version.color ?? "",
        status: version.status ?? "active",
        hourly_rate: version.hourly_rate ?? null,
        currency: version.currency ?? null,
        billable: toIntBool(version.billable),
        updated_at: nowISO(),
      })
      .where(eq(project.id, projectId));
  });
}

/**
 * Persist a project's billing definition into the SYNCED project (PROJECT
 * VERSIONING). This is what makes future time entries seal a real rate. Replaces
 * the legacy localStorage-only rate for this project (localStorage stays as the
 * fallback for projects without a synced rate). Only sets the provided fields.
 */
export async function updateProjectRateMutation(
  db: PowerSyncDrizzleDB,
  projectId: string,
  fields: { hourlyRate?: number | null; currency?: string | null; billable?: boolean | null },
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const patch: Record<string, unknown> = {};
  if (fields.hourlyRate !== undefined) {
    patch.hourly_rate = fields.hourlyRate;
  }
  if (fields.currency !== undefined) {
    patch.currency = fields.currency;
  }
  if (fields.billable !== undefined) {
    patch.billable = toIntBool(fields.billable);
  }
  await patchRowVerified(db, project, project.id, projectId, patch, options);
}
